import { enterAge, settleT } from "../art/craftFeel.ts";
import { UI, withA } from "../art/pal.ts";
import { fitText, measureText } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Scene, ScenePayload } from "../game/scenes.ts";
import { cleanName, EMOTES, NAME_MAX } from "../net/protocol.ts";
import { chainLabel, shortAddress, type WalletChain } from "../net/wallet.ts";
import { DISCORD_INVITE, openLink } from "../net/links.ts";
import { hasWallet, needsWalletApp, walletAppLink } from "../net/walletBrowser.ts";
import { installPrompt } from "../pwa.ts";
import { backdrop, glass, hint, titleBar } from "../ui/kit.ts";
import { actorFrame, wornOutfit } from "../world/actors.ts";
import { BRASS } from "./crownHouse.ts";

type Pick = "name" | "savekey" | "restore" | "invite" | "bug" | "install" | "discord" | "link-evm" | "link-solana" | "unlink" | "login-evm" | "login-solana" | "close";

/** Pause → ONLINE: who you are in the live world, who is around, the duel ladder and the controls. */
export function onlineScene(ctx: Ctx): Scene {
  let age = 0;
  let menuOpen = false;
  let cursor = 0;
  let toast: { text: string; at: number } | null = null;
  let unsub: () => void = () => {};
  /** The DOM field a bug report is typed into (same trick as the name screen). */
  let entry: HTMLInputElement | null = null;

  function closeEntry(): void {
    entry?.remove();
    entry = null;
  }

  /** A one-line DOM field over the game (bug reports, recovery keys), same trick as the name screen. */
  function openEntry(placeholder: string, max: number, onEnter: (text: string) => void, value = ""): boolean {
    const host = typeof document !== "undefined" && typeof document.createElement === "function" ? document.getElementById("bezel") : null;
    if (!host) return false;
    entry = document.createElement("input");
    entry.id = "key-entry";
    entry.maxLength = max;
    entry.placeholder = placeholder;
    entry.autocomplete = "off";
    entry.value = value;
    entry.addEventListener("keydown", (ev) => {
      ev.stopPropagation();
      if (ev.key === "Enter") {
        const text = entry!.value.trim();
        closeEntry();
        onEnter(text);
      } else if (ev.key === "Escape") {
        ctx.audio.back();
        closeEntry();
      }
    });
    host.appendChild(entry);
    setTimeout(() => {
      entry?.focus();
      if (value) entry?.select();
    }, 0);
    return true;
  }

  /** Save my progress: the recovery key to the clipboard (or shown, selected, to copy by hand). */
  function saveKey(): void {
    const key = ctx.net.recoveryKey();
    const shown = () => {
      if (openEntry("", 120, () => {}, key)) say("Copy this recovery key and keep it in your notes. Enter or Esc to close.");
      else say(`Your recovery key: ${key}`);
      ctx.net.markKeySaved();
    };
    const clip = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    if (!clip) return shown();
    clip.writeText(key).then(() => {
      ctx.net.markKeySaved();
      say("Recovery key copied. Keep it in your notes: it brings this scout back on any device.");
    }, shown);
  }

  function openRestore(): void {
    const ok = openEntry("Paste your recovery key (MC1-...), then Enter", 200, (text) => {
      if (!text) return;
      if (ctx.net.restore(text)) say("Checking the key...");
      else say("That does not look like a recovery key. It starts with MC1-.");
    });
    if (!ok) say("Restoring needs the browser page.");
  }

  function openBugEntry(): void {
    const host = typeof document !== "undefined" && typeof document.createElement === "function" ? document.getElementById("bezel") : null;
    if (!host) return say("Bug reports need the browser page.");
    entry = document.createElement("input");
    entry.id = "key-entry";
    entry.maxLength = 500;
    entry.placeholder = "What went wrong? (Enter to send, Esc to cancel)";
    entry.autocomplete = "off";
    entry.addEventListener("keydown", (ev) => {
      ev.stopPropagation();
      if (ev.key === "Enter") {
        const text = entry!.value.trim();
        closeEntry();
        if (text.length < 4) return say("Say a few words about what went wrong.");
        ctx.audio.confirm();
        if (!ctx.net.reportBug(text)) say("Not connected. Try again in a moment.");
      } else if (ev.key === "Escape") {
        ctx.audio.back();
        closeEntry();
      }
    });
    host.appendChild(entry);
    setTimeout(() => entry?.focus(), 0);
  }

  function copyInvite(): void {
    const link = ctx.net.inviteLink();
    if (!link) return say("Connect first to get your invite link.");
    const clip = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    const share = typeof navigator !== "undefined" ? (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share : undefined;
    // Phones get the share sheet; desktops get the clipboard.
    if (share && /Android|iPhone|iPad/i.test(navigator.userAgent)) {
      share.call(navigator, { title: "Pocket Meme", text: "Catch memes with me in Pocket Meme:", url: link }).catch(() => {});
      return;
    }
    if (!clip) return say(`Your invite link: ${link}`);
    clip.writeText(link).then(() => say("Invite link copied. Send it to a friend!"), () => say(`Your invite link: ${link}`));
  }

  function say(text: string): void {
    toast = { text, at: ctx.now };
  }

  /** The A menu: the name always, wallets when the server offers them. */
  function options(): { id: Pick; label: string }[] {
    const net = ctx.net;
    const out: { id: Pick; label: string }[] = [{ id: "name", label: "Change name" }];
    if (net.live) out.push({ id: "savekey", label: "Save my progress" }, { id: "restore", label: "Restore a recovery key" });
    if (net.live) out.push({ id: "invite", label: "Invite a friend" }, { id: "bug", label: "Report a bug" });
    if (installPrompt()) out.push({ id: "install", label: "Install the app" });
    out.push({ id: "discord", label: "Join the Discord" });
    if (net.live && net.walletsEnabled) {
      if (net.wallet) out.push({ id: "unlink", label: `Unlink ${shortAddress(net.wallet.address)}` });
      else out.push({ id: "link-evm", label: "Link an Ethereum wallet" }, { id: "link-solana", label: "Link a Solana wallet" });
      out.push({ id: "login-evm", label: "Sign in: Ethereum wallet" }, { id: "login-solana", label: "Sign in: Solana wallet" });
    }
    out.push({ id: "close", label: "Close" });
    return out;
  }

  /** No wallet in this browser: on a phone, reopen the game in a wallet app; elsewhere, say what to install. */
  function needWallet(chain: WalletChain): boolean {
    if (hasWallet(chain)) return false;
    // The handoff code (fetched when the menu opened) keeps this scout in the wallet app's browser.
    const app = walletAppLink(chain, ctx.net.handoffCode());
    if (app && typeof window !== "undefined") {
      say(`Opening the game in ${chain === "evm" ? "MetaMask" : "Phantom"}...`);
      window.location.href = app;
    } else say(chain === "evm" ? "No Ethereum wallet here. Install MetaMask or Rabby." : "No Solana wallet here. Install Phantom or Solflare.");
    return true;
  }

  function choose(id: Pick): void {
    const net = ctx.net;
    menuOpen = false;
    if (id === "name") return ctx.push("name", { notice: "rename" });
    if (id === "close") return;
    if (id === "invite") return copyInvite();
    if (id === "bug") return openBugEntry();
    if (id === "savekey") return saveKey();
    if (id === "restore") return openRestore();
    if (id === "discord") {
      if (!openLink(DISCORD_INVITE)) say(`Join us: ${DISCORD_INVITE.replace("https://", "")}`);
      return;
    }
    if (id === "install") {
      const go = installPrompt();
      if (go) void go().then((ok) => say(ok ? "Installed! Find Pocket Meme on your home screen." : "Maybe later."));
      return;
    }
    if (id === "unlink") return net.unlinkWallet();
    const chain: WalletChain = id.endsWith("evm") ? "evm" : "solana";
    if (needWallet(chain)) return;
    say(`Check your ${chainLabel(chain)} wallet: sign the message (no fee).`);
    if (id.startsWith("link")) void net.linkWallet(chain);
    else void net.walletLogin(chain);
  }

  return {
    id: "online",
    native: true,
    enter() {
      age = enterAge(ctx.shot);
      ctx.net.send({ t: "top" });
      ctx.net.send({ t: "ping", at: ctx.now });
      ctx.net.send({ t: "econ" });
      menuOpen = false;
      toast = null;
      unsub = ctx.net.on((m) => {
        if (m.t !== "notice") return;
        say(m.text);
        ctx.net.notices = ctx.net.notices.filter((n) => n !== m.text);
      });
    },
    exit() {
      unsub();
      closeEntry();
    },
    update() {
      age += ctx.dt;
      if (entry) {
        // The DOM field owns the keys; keep its focus when the canvas is clicked.
        if (typeof document !== "undefined" && document.activeElement !== entry) entry.focus();
        return;
      }
      if (menuOpen) {
        const opts = options();
        const d = ctx.input.menuDir(ctx.now);
        if (d?.y) {
          cursor = (cursor + d.y + opts.length) % opts.length;
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          menuOpen = false;
        } else if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          choose(opts[Math.min(cursor, opts.length - 1)]!.id);
        }
        return;
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.pop();
      } else if (ctx.input.consume("Enter")) {
        ctx.audio.confirm();
        menuOpen = true;
        cursor = 0;
        if (needsWalletApp() && !hasWallet("evm") && !hasWallet("solana")) ctx.net.prepareHandoff();
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const net = ctx.net;
      const t = settleT(age, reduced, 140);
      backdrop(g, ctx.now, reduced, [40, 28, 70, 255], [10, 10, 24, 255]);
      const status = !net.enabled ? "OFFLINE BUILD" : net.status === "online" ? `${net.online} ONLINE` : net.full ? "SERVER FULL · RETRYING" : net.status === "offline" ? "OFFLINE · DEV IS COOKING" : net.status.toUpperCase();
      titleBar(g, "LIVE WORLD", status, UI.pink);

      // You.
      const x0 = 8 - Math.round((1 - t) * 24);
      glass(g, x0, 28, 170, 76, { r: 8 });
      const worn = wornOutfit(ctx.state.cosmetics);
      const img = actorFrame(worn.key, worn.outfit, 1, 0);
      g.blit(img, x0 + 10, 36, { scale: 2 });
      g.textShadow(x0 + 50, 36, fitText(ctx.state.name, 112), UI.goldHi, UI.ink);
      g.text(x0 + 50, 50, net.live ? `Rating ${net.rating}${net.tag ? `  #${net.tag}` : ""}` : "Rating --", UI.text);
      g.text(x0 + 50, 62, net.live ? `Duels ${net.wins}W ${net.losses}L` : "Not connected", UI.textDim);
      if (ctx.state.title) g.text(x0 + 50, 70, `★ ${ctx.state.title}`, UI.goldHi);
      g.rrect(x0 + 50, 78, 112, 16, 6, withA(UI.gold, 60));
      hint(g, x0 + 53, 80, "A", net.live && net.walletsEnabled ? "name & wallet" : "name & Discord", "a");

      // Nearby.
      glass(g, x0, 110, 170, 82, { r: 8 });
      g.textShadow(x0 + 10, 116, "NEARBY SCOUTS", UI.textDim, UI.ink);
      const peers = [...net.peers.values()].sort((a, b) =>
        Math.max(Math.abs(a.x - ctx.state.x), Math.abs(a.y - ctx.state.y)) - Math.max(Math.abs(b.x - ctx.state.x), Math.abs(b.y - ctx.state.y)));
      if (!peers.length) g.para(x0 + 10, 130, net.live ? "Nobody in sight. Walk the routes or invite a friend." : "Connect to see other scouts.", 150, UI.textMute, 11, 4);
      peers.slice(0, 5).forEach((p, i) => {
        const y = 130 + i * 12;
        g.disc(x0 + 13, y + 3, 3, p.status === "duel" ? UI.pink : p.status === "trade" ? UI.teal : UI.green);
        g.text(x0 + 20, y, fitText(p.name, 90), UI.text);
        g.textR(x0 + 160, y, `${p.rating}`, UI.textDim);
      });

      // Ladder.
      const x1 = 186 + Math.round((1 - t) * 24);
      glass(g, x1, 28, 190, 96, { r: 8 });
      g.textShadow(x1 + 10, 34, "DUEL LADDER", UI.textDim, UI.ink);
      const econ = net.live ? net.econ : null;
      if (econ && econ.mode !== "off") {
        const label = econ.mode === "shadow" ? `shadow ${econ.wallet.shadow} Brass` : `${econ.wallet.available} Brass`;
        g.textR(x1 + 180, 34, label, BRASS);
      }
      if (!net.top.length) g.para(x1 + 10, 50, "No ranked duels yet. Walk up to a scout, press A, pick DUEL.", 170, UI.textMute, 11, 4);
      net.top.slice(0, 6).forEach((row, i) => {
        const y = 48 + i * 12;
        const me = row.name === ctx.state.name;
        g.text(x1 + 10, y, `${i + 1}.`, i === 0 ? UI.goldHi : UI.textDim);
        g.text(x1 + 26, y, fitText(row.name, 80), me ? UI.goldHi : UI.text);
        g.textR(x1 + 140, y, `${row.rating}`, UI.gold);
        g.textR(x1 + 180, y, `${row.wins}-${row.losses}`, UI.textDim);
      });

      // How to.
      glass(g, x1, 130, 190, 62, { r: 8 });
      g.textShadow(x1 + 10, 136, "HOW TO", UI.textDim, UI.ink);
      const rows = [
        "A on a scout: duel / trade / wave",
        `T: chat   1-6: ${EMOTES.join(" ")}`,
      ];
      rows.forEach((r, i) => g.text(x1 + 10, 150 + i * 12, fitText(r, 172), UI.text));
      if (net.live && net.walletsEnabled) {
        const w = net.wallet;
        g.text(x1 + 10, 174, "Wallet", UI.textDim);
        g.text(x1 + 46, 174, fitText(net.walletBusy ? "waiting for signature..." : w ? `${shortAddress(w.address)} ${w.chain === "evm" ? "ETH" : "SOL"}` : "none linked", 130), w ? UI.goldHi : UI.textMute);
      }
      if (!net.enabled) g.text(10, SCREEN_H - 14, "Start the server (npm run server) to play online.", UI.textMute);
      else hint(g, SCREEN_W - 70, SCREEN_H - 18, "B", "back", "b");

      if (entry) {
        g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, 150]);
        glass(g, 20, 70, 344, 76, { r: 10, border: UI.gold });
        g.textShadow(34, 78, "REPORT A BUG", UI.goldHi, UI.ink);
        g.text(34, 92, "Your save is attached so we can reproduce it.", UI.textDim);
      } else if (menuOpen) {
        const opts = options();
        g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, 150]);
        const h = 34 + opts.length * 13;
        const y0 = Math.round((SCREEN_H - h) / 2);
        glass(g, 92, y0, 200, h, { r: 10, border: UI.gold });
        g.textShadow(104, y0 + 8, "YOU IN THE LIVE WORLD", UI.goldHi, UI.ink);
        opts.forEach((o, k) => {
          const on = k === cursor;
          const y = y0 + 24 + k * 13;
          if (on) g.rrect(100, y - 2, 184, 12, 4, withA(UI.bg3, 220));
          g.text(104, y, `${on ? "▶ " : "  "}${o.label}`, on ? UI.goldHi : UI.text);
        });
      }
      if (toast && ctx.now - toast.at < 4000) {
        // Left-aligned so it never covers the B hint.
        const w = Math.min(296, measureText(toast.text) + 20);
        glass(g, 8, 196, w, 16, { r: 6, alpha: 235 });
        g.text(18, 200, fitText(toast.text, w - 20), UI.text);
      }
    },
  };
}

/**
 * Name entry. A real DOM text field sits over the card so phones get their keyboard; without a DOM
 * (tests, tools) the scene keeps the current name and moves on.
 */
export function nameScene(ctx: Ctx): Scene {
  let el: HTMLInputElement | null = null;
  let rename = false;
  let next: ScenePayload | undefined;
  let age = 0;

  function done(name: string | null): void {
    if (name) {
      ctx.state.name = cleanName(name);
      ctx.save();
    }
    el?.remove();
    el = null;
    if (rename) ctx.pop();
    else ctx.replace("overworld", next);
  }

  return {
    id: "name",
    native: true,
    enter(payload?: ScenePayload) {
      rename = payload?.notice === "rename";
      next = rename ? undefined : payload;
      age = enterAge(ctx.shot);
      const host = typeof document !== "undefined" && typeof document.createElement === "function" ? document.getElementById("bezel") : null;
      if (!host || ctx.shot) {
        done(null);
        return;
      }
      el = document.createElement("input");
      el.id = "name-entry";
      el.maxLength = NAME_MAX;
      el.value = ctx.state.name === "Scout" ? "" : ctx.state.name;
      el.placeholder = "Your name";
      el.autocomplete = "off";
      el.spellcheck = false;
      el.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          ctx.audio.confirm();
          done(el!.value.trim() || ctx.state.name);
        } else if (e.key === "Escape") {
          ctx.audio.back();
          done(null);
        }
      });
      host.appendChild(el);
      setTimeout(() => el?.focus(), 0);
    },
    exit() {
      el?.remove();
      el = null;
    },
    update() {
      age += ctx.dt;
      // Clicking the canvas steals focus; give it back so typing keeps working.
      if (el && typeof document !== "undefined" && document.activeElement !== el) el.focus();
      if (el?.parentElement) el.style.fontSize = `${Math.max(12, Math.round(el.parentElement.clientHeight * 0.055))}px`;
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      backdrop(g, ctx.now, reduced, [28, 40, 80, 255], [10, 10, 24, 255]);
      const t = settleT(age, reduced, 160);
      const y = 44 + Math.round((1 - t) * 14);
      glass(g, 62, y, 260, 124, { r: 10 });
      g.textShadow(76, y + 12, rename ? "NEW NAME" : "WHAT DO THEY CALL YOU?", UI.goldHi, UI.ink);
      g.para(76, y + 28, "Other scouts see this name in the live world. Letters, numbers, spaces.", 232, UI.textDim, 11, 3);
      // The DOM field is laid over this slot by CSS (#name-entry).
      g.rrect(76, y + 64, 232, 22, 6, UI.bg0);
      g.rrectLine(76, y + 64, 232, 22, 6, UI.gold);
      if (!el) g.text(84, y + 71, ctx.state.name, UI.text);
      hint(g, 76, y + 98, "Enter", "confirm", "a");
      hint(g, 190, y + 98, "Esc", rename ? "keep name" : "keep Scout", "b");
    },
  };
}
